import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import PageShell from "@/components/PageShell";
import NextRoom from "@/components/NextRoom";
import { ARTIFACTS, ARTIFACT_CATEGORIES, TEXTURES, getBuilding, getObservation, getPillar, getDomain } from "@/data/content";

const PLACEHOLDER_TEXTURES = [TEXTURES.stairs, TEXTURES.buildingDetail, TEXTURES.corridor, TEXTURES.emptyRoom, TEXTURES.chairs];

export default function ArtifactDetail() {
  const { slug } = useParams();
  const a = ARTIFACTS.find((x) => x.slug === slug);
  if (!a) return <Navigate to="/artifacts" replace />;

  const cat = ARTIFACT_CATEGORIES.find((c) => c.slug === a.category);
  const proj = a.relatedProject ? getBuilding(a.relatedProject) : null;
  const obs = a.relatedObservation ? getObservation(a.relatedObservation) : null;
  const pillar = a.relatedPillar ? getPillar(a.relatedPillar) : null;
  const domain = obs ? getDomain(obs.domain) : null;

  const idx = ARTIFACTS.findIndex((x) => x.slug === a.slug);
  const prev = ARTIFACTS[(idx - 1 + ARTIFACTS.length) % ARTIFACTS.length];
  const next = ARTIFACTS[(idx + 1) % ARTIFACTS.length];
  const image = a.image || PLACEHOLDER_TEXTURES[idx % PLACEHOLDER_TEXTURES.length];

  return (
    <PageShell
      testId="page-artifact-detail"
      title={`${a.title} | Artifacts | Quentin Young`}
      description={a.context}
      ogImage="/og-artifacts.jpg"
      breadcrumbs={[
        { name: "Home", url: "/" },
        { name: "Artifacts", url: "/artifacts" },
        { name: a.title, url: `/artifacts/${a.slug}` },
      ]}
    >
      <article className="container-edge pt-16 md:pt-24 pb-16">
        <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-paper-mute mb-6">
          <Link to="/artifacts" className="link-quiet">Artifacts</Link> · {cat?.name || a.category} · {a.era}
        </p>
        <h1 className="display-serif text-4xl md:text-6xl lg:text-7xl text-paper-primary text-balance leading-[1.02]">
          {a.title}
        </h1>
      </article>

      <section className="bg-ink-base pb-20">
        <div className="container-edge grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-6">
            <div className="relative aspect-[4/3] overflow-hidden border border-ink-line bg-ink-surface">
              <div
                className="absolute inset-0 grayscale opacity-70"
                style={{ backgroundImage: `url(${image})`, backgroundSize: "cover", backgroundPosition: "center" }}
                aria-hidden="true"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-base via-ink-base/30 to-transparent" />
              {!a.image && (
                <p className="absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-[0.24em] text-paper-mute">
                  Placeholder image · Awaiting real artifact scan
                </p>
              )}
            </div>
          </div>
          <div className="lg:col-span-6 space-y-8">
            <div>
              <p className="eyebrow-gold mb-3">Context</p>
              <p className="text-paper-secondary text-lg leading-relaxed">{a.context}</p>
            </div>
            {a.whatChanged && (
              <div className="border-t border-ink-line pt-6">
                <p className="eyebrow mb-3">What changed</p>
                <p className="text-paper-secondary text-base leading-relaxed">{a.whatChanged}</p>
              </div>
            )}
            {a.taught && (
              <div className="border-t border-ink-line pt-6">
                <p className="eyebrow mb-3">What it taught</p>
                <p className="font-serif italic text-xl text-paper-primary leading-snug">{a.taught}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Cross-links */}
      {(obs || proj || pillar) && (
        <section className="bg-ink-surface border-t border-ink-line py-16">
          <div className="container-edge grid grid-cols-1 md:grid-cols-3 gap-px bg-ink-line">
            {obs && (
              <Link to={`/thinking/${obs.slug}`} className="bg-ink-surface p-8 group" data-testid="artifact-related-observation">
                <p className="eyebrow mb-3">Related Observation</p>
                <p className="font-serif text-xl md:text-2xl text-paper-primary group-hover:text-gold-warm transition-colors text-balance leading-snug">
                  {obs.headline}
                </p>
                <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-paper-tertiary">{domain?.name}</p>
              </Link>
            )}
            {proj && (
              <Link to={`/building/${proj.slug}`} className="bg-ink-surface p-8 group" data-testid="artifact-related-building">
                <p className="eyebrow mb-3">Where This Shows Up</p>
                <p className="font-serif text-xl md:text-2xl text-paper-primary group-hover:text-gold-warm transition-colors">{proj.name}</p>
                <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-paper-tertiary">{proj.category}</p>
              </Link>
            )}
            {pillar && (
              <Link to={`/philosophy#pillar-${pillar.slug}`} className="bg-ink-surface p-8 group" data-testid="artifact-related-pillar">
                <p className="eyebrow mb-3">Under the Lens Of</p>
                <p className="font-serif text-xl md:text-2xl text-paper-primary group-hover:text-gold-warm transition-colors">{pillar.name}</p>
                <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-paper-tertiary">Pillar {pillar.number}</p>
              </Link>
            )}
          </div>
        </section>
      )}

      {/* Prev/Next */}
      <section className="border-t border-ink-line bg-ink-base">
        <div className="container-edge grid grid-cols-2 gap-px bg-ink-line">
          <Link to={`/artifacts/${prev.slug}`} className="bg-ink-base p-8 md:p-10 group" data-testid="artifact-prev">
            <p className="eyebrow mb-3">← Previous artifact</p>
            <p className="font-serif text-xl text-paper-primary group-hover:text-gold-warm transition-colors">{prev.title}</p>
          </Link>
          <Link to={`/artifacts/${next.slug}`} className="bg-ink-base p-8 md:p-10 group text-right" data-testid="artifact-next">
            <p className="eyebrow mb-3">Continue through the archive →</p>
            <p className="font-serif text-xl text-paper-primary group-hover:text-gold-warm transition-colors">{next.title}</p>
          </Link>
        </div>
      </section>

      <NextRoom
        eyebrow="Move to the next room"
        title="Trust the evolution. Then follow it."
        rooms={[
          proj ? { label: `See what this became: ${proj.name}`, to: `/building/${proj.slug}`, hint: "proof" } : { label: "See the work in practice", to: "/building", hint: "proof" },
          obs ? { label: "Read the related observation", to: `/thinking/${obs.slug}`, hint: "thinking behind it" } : { label: "Study the framework", to: "/philosophy", hint: "framework" },
          { label: "Return to the archive", to: "/artifacts", hint: "long view" },
        ]}
        testId="artifact-detail-next"
      />
    </PageShell>
  );
}
